/**
 * Cross-tab release-state sync.
 *
 * React Query caches are per-tab. After any release mutation, invalidateReleaseState()
 * broadcasts a typed, payload-free message so every OTHER tab of the same user runs the
 * same invalidation. The single listener is mounted once (SSEProvider) via
 * useReleaseCrossTabSync(). Uses BroadcastChannel, localStorage `storage` event as fallback.
 */

import { useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { createReleaseInvalidationDebouncer } from '@/lib/releaseInvalidationDebouncer';
import { useEffectiveTeamId } from '@/hooks/useEffectiveTeamId';
import { useAuth } from '@/hooks/useAuth';

const CHANNEL_NAME = 'aa-release-sync';
const STORAGE_KEY = 'aa-release-sync';
/** Sender-side window: a bulk action's N mutations become one message. */
const SEND_COALESCE_MS = 100;
/** Receiver-side trailing window (merges teamIds/postIds, comment escalates to full). */
const RECEIVE_DEBOUNCE_MS = 200;

const SENDER_ID: string =
  typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(36).slice(2)}`;

export interface ReleaseSyncMessage {
  type: 'release-state';
  senderId: string;
  userId: number;
  teamIds: number[];
  postId?: number;
  scope: 'full' | 'comment';
  at: number;
}

const isReleaseSyncMessage = (value: unknown): value is ReleaseSyncMessage => {
  if (typeof value !== 'object' || value === null) return false;
  const v = value as Partial<ReleaseSyncMessage>;
  return (
    v.type === 'release-state' &&
    typeof v.senderId === 'string' &&
    typeof v.userId === 'number' &&
    Array.isArray(v.teamIds)
  );
};

/** Set by the mounted listener; no user => nothing is broadcast (and nothing leaks across logins). */
let currentUserId: number | null = null;

export interface BroadcastReleaseStateInput {
  teamIds: ReadonlyArray<number | null>;
  postId?: number;
  scope: 'full' | 'comment';
}

/** One long-lived sender channel (not opened/closed per message). */
let senderChannel: BroadcastChannel | null = null;

interface PendingSend {
  userId: number;
  teamIds: Set<number>;
  postIds: Set<number>;
  hasBroad: boolean;
  scope: 'full' | 'comment';
}
let pendingSend: PendingSend | null = null;
let sendTimer: ReturnType<typeof setTimeout> | null = null;

function postMessageToOtherTabs(message: ReleaseSyncMessage): void {
  try {
    if (typeof BroadcastChannel !== 'undefined') {
      senderChannel ??= new BroadcastChannel(CHANNEL_NAME);
      senderChannel.postMessage(message);
    } else {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(message));
      // The storage event fires in OTHER tabs on set; drop the value right away so it never lingers.
      localStorage.removeItem(STORAGE_KEY);
    }
  } catch {
    // Best-effort; refetch-on-focus is the safety net.
  }
}

function flushSend(): void {
  sendTimer = null;
  const batch = pendingSend;
  pendingSend = null;
  if (!batch) return;
  postMessageToOtherTabs({
    type: 'release-state',
    senderId: SENDER_ID,
    userId: batch.userId,
    teamIds: [...batch.teamIds],
    postId: !batch.hasBroad && batch.postIds.size === 1 ? [...batch.postIds][0] : undefined,
    scope: batch.scope,
    at: Date.now(),
  });
}

/** Tell other tabs of this user that release state changed (coalesced). Never throws. */
export function broadcastReleaseState({ teamIds, postId, scope }: BroadcastReleaseStateInput): void {
  if (currentUserId === null) return;
  if (pendingSend && pendingSend.userId !== currentUserId) flushSend();
  pendingSend ??= { userId: currentUserId, teamIds: new Set<number>(), postIds: new Set<number>(), hasBroad: false, scope: 'comment' };
  for (const id of teamIds) if (typeof id === 'number') pendingSend.teamIds.add(id);
  if (postId === undefined) pendingSend.hasBroad = true;
  else pendingSend.postIds.add(postId);
  if (scope === 'full') pendingSend.scope = 'full';
  if (sendTimer) clearTimeout(sendTimer);
  sendTimer = setTimeout(flushSend, SEND_COALESCE_MS);
}

function subscribe(onMessage: (message: ReleaseSyncMessage) => void): () => void {
  const accept = (value: unknown): void => {
    if (isReleaseSyncMessage(value) && value.senderId !== SENDER_ID) onMessage(value);
  };
  if (typeof BroadcastChannel !== 'undefined') {
    const channel = new BroadcastChannel(CHANNEL_NAME);
    channel.onmessage = (event: MessageEvent<unknown>) => accept(event.data);
    return () => channel.close();
  }
  const onStorage = (event: StorageEvent): void => {
    if (event.key !== STORAGE_KEY || !event.newValue) return;
    try {
      accept(JSON.parse(event.newValue) as unknown);
    } catch {
      // ignore malformed payload
    }
  };
  window.addEventListener('storage', onStorage);
  return () => window.removeEventListener('storage', onStorage);
}

/** Mount ONCE at app level (inside Auth + QueryClient providers). */
export function useReleaseCrossTabSync(): void {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const { teamId } = useEffectiveTeamId();
  const userId = user?.id ?? null;

  useEffect(() => {
    currentUserId = userId;
    if (userId === null) return undefined;
    // Trailing debounce: a burst of messages (bulk actions) is ONE invalidation, never an echo back.
    const debouncer = createReleaseInvalidationDebouncer(RECEIVE_DEBOUNCE_MS, false);
    const unsubscribe = subscribe((message) => {
      if (message.userId !== userId) return;
      debouncer.schedule(queryClient, {
        teamId,
        alsoTeamIds: message.teamIds,
        postId: message.postId,
        scope: message.scope,
      });
    });
    return () => {
      unsubscribe();
      debouncer.cancel();
      currentUserId = null;
      if (sendTimer) clearTimeout(sendTimer);
      // Flush (don't drop) a send queued in the last coalesce window. The batch carries its own
      // userId, so it never leaks to another login: receivers ignore messages for other users.
      flushSend();
      try {
        localStorage.removeItem(STORAGE_KEY); // logout: clear the storage fallback
      } catch {
        // storage unavailable
      }
    };
  }, [queryClient, teamId, userId]);
}
